/**
 * Facet Analyzer - Pass 2 of Multi-Pass Analysis
 *
 * Extracts facets (structured insights) that provide evidence for entities.
 * This runs AFTER entity extraction (Pass 1) and receives entity context.
 *
 * Key improvements over genericAnalyzer:
 * - Full facet metadata in prompts (descriptions + detection hints)
 * - Entity context from Pass 1 guides facet extraction
 * - Focused task = better results (40-60 facets vs 6)
 */

import { jsonrepair } from 'jsonrepair'
import { callClaude, type ClaudeConfig } from '../ai/claude'
import type { ContentType } from './types'
import type { FacetTypeDefinition } from './facetSchema'
import {
  buildFacetSystemPrompt,
  buildFacetUserPrompt,
  getDefaultEnabledFacets,
  getFacetsByIds,
} from '../prompts/facetExtractionPrompts'

// ============================================================================
// TYPES
// ============================================================================

export interface FacetAnalysisRequest {
  sourceId: string
  content: string
  contentType: ContentType
  enabledFacetIds?: string[]
  extractedEntities?: Array<{
    id: string
    type: string
    name: string
  }>
}

export interface ExtractedFacet {
  facetTypeId: string
  title?: string
  content: string
  evidence: string
  confidence: number
  positionStart?: number
  positionEnd?: number
  suggestedEntityType?: string
  suggestedReason?: string
  problemContext?: string
  problemCause?: string
  problemConsequence?: string
}

/**
 * Visual chunk identified in text that requires visual analysis
 * These are regions the text parser couldn't fully interpret
 */
export interface VisualChunk {
  pageNumber: number
  description: string
  chunkType: 'diagram' | 'chart' | 'canvas' | 'whiteboard' | 'screenshot' | 'table' | 'image' | 'unknown'
  textContext?: string  // Surrounding text that references this visual
  confidence: number
}

export interface FacetAnalysisResult {
  sourceId: string
  facets: ExtractedFacet[]
  visualChunks: VisualChunk[]  // Visual regions that need separate analysis
  analysisTime: number
  error?: string
}

// ============================================================================
// MAIN ANALYZER FUNCTION
// ============================================================================

/**
 * Analyze content and extract facets (Pass 2)
 *
 * @param config - Claude API configuration
 * @param request - Analysis request with content and optional entity context
 * @returns Extracted facets with metadata
 */
export async function analyzeFacets(
  config: ClaudeConfig,
  request: FacetAnalysisRequest
): Promise<FacetAnalysisResult> {
  const startTime = Date.now()

  // Get enabled facets (either from request or default)
  const enabledFacets = request.enabledFacetIds
    ? getFacetsByIds(request.enabledFacetIds)
    : getDefaultEnabledFacets()

  if (enabledFacets.length === 0) {
    return {
      sourceId: request.sourceId,
      facets: [],
      visualChunks: [],
      analysisTime: Date.now() - startTime,
    }
  }

  // Build prompts with full facet metadata and entity context
  const systemPrompt = buildFacetSystemPrompt(
    enabledFacets,
    request.contentType,
    request.extractedEntities
  )
  const userPrompt = buildFacetUserPrompt(request.content)

  // Call Claude AI with deterministic settings for consistency
  let response: string
  try {
    response = await callClaude(
      {
        ...config,
        temperature: 0, // Deterministic extraction
      },
      systemPrompt,
      userPrompt
    )
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown API error'
    console.error('[facetAnalyzer] Claude API call failed:', errorMessage)
    return {
      sourceId: request.sourceId,
      facets: [],
      visualChunks: [],
      analysisTime: Date.now() - startTime,
      error: errorMessage,
    }
  }

  // Parse and validate response (now returns both facets and visual chunks)
  const { facets, visualChunks } = parseAndValidateFacets(response, enabledFacets)

  return {
    sourceId: request.sourceId,
    facets,
    visualChunks,
    analysisTime: Date.now() - startTime,
  }
}

// ============================================================================
// RESPONSE PARSING
// ============================================================================

/**
 * Parse Claude's JSON response and validate facets + visual chunks
 */
function parseAndValidateFacets(
  response: string,
  enabledFacets: FacetTypeDefinition[]
): { facets: ExtractedFacet[]; visualChunks: VisualChunk[] } {
  // Extract JSON from response (handle markdown code blocks and text before/after)
  let jsonStr: string | null = null

  // Try 1: Extract from markdown code block
  const markdownMatch = response.match(/```json\n?([\s\S]*?)\n?```/)
  if (markdownMatch) {
    jsonStr = markdownMatch[1]
  }

  // Try 2: Find first complete JSON object with "facets" key
  if (!jsonStr) {
    const objectMatch = response.match(/(\{[\s\S]*?"facets"[\s\S]*?\}(?=\n|$|Note:|Remember:))/m)
    if (objectMatch) {
      jsonStr = objectMatch[1]
    }
  }

  // Try 3: Extract any JSON-like structure
  if (!jsonStr) {
    const anyJsonMatch = response.match(/\{[\s\S]*\}/)
    if (anyJsonMatch) {
      jsonStr = anyJsonMatch[0]
    }
  }

  if (!jsonStr) {
    console.error('[facetAnalyzer] No JSON found in response')
    console.error('[facetAnalyzer] Response preview:', response.substring(0, 500))
    return { facets: [], visualChunks: [] }
  }

  let parsed: any
  try {
    // First attempt: parse as-is
    parsed = JSON.parse(jsonStr)
  } catch (error) {
    console.error('[facetAnalyzer] JSON parse error:', error)
    console.error('[facetAnalyzer] Failed JSON (first 500 chars):', jsonStr.substring(0, 500))
    console.error('[facetAnalyzer] Failed JSON (last 500 chars):', jsonStr.substring(jsonStr.length - 500))

    // Try 2: Use jsonrepair library (handles trailing commas, unescaped quotes, etc.)
    try {
      const repairedJson = jsonrepair(jsonStr)
      parsed = JSON.parse(repairedJson)
      console.log('[facetAnalyzer] ✅ Successfully repaired JSON with jsonrepair library')
    } catch (repairError) {
      console.error('[facetAnalyzer] jsonrepair failed:', repairError)

      // Try 3: Manual repair for truncated JSON
      try {
        const manuallyRepaired = repairTruncatedJson(jsonStr)
        parsed = JSON.parse(manuallyRepaired)
        console.log('[facetAnalyzer] ✅ Successfully repaired truncated JSON manually')
      } catch (manualRepairError) {
        console.error('[facetAnalyzer] All repair attempts failed:', manualRepairError)
        console.error('[facetAnalyzer] Original response length:', response.length)
        return { facets: [], visualChunks: [] }
      }
    }
  }

  // Validate structure
  if (!parsed.facets || !Array.isArray(parsed.facets)) {
    console.error('[facetAnalyzer] Invalid response structure - missing facets array')
    return { facets: [], visualChunks: [] }
  }

  // Validate and transform each facet
  const enabledFacetIds = new Set(enabledFacets.map(f => f.id))
  const validatedFacets: ExtractedFacet[] = []

  for (const facet of parsed.facets) {
    try {
      const validated = validateFacet(facet, enabledFacetIds)
      if (validated) {
        validatedFacets.push(validated)
      }
    } catch (error) {
      console.error('[facetAnalyzer] Facet validation error:', error, facet)
      // Continue processing other facets
    }
  }

  // Parse visual chunks (regions that need separate visual analysis)
  const visualChunks: VisualChunk[] = []
  if (parsed.visualChunks && Array.isArray(parsed.visualChunks)) {
    for (const chunk of parsed.visualChunks) {
      const validated = validateVisualChunk(chunk)
      if (validated) {
        visualChunks.push(validated)
      }
    }
    console.log(`[facetAnalyzer] Identified ${visualChunks.length} visual chunks`)
  }

  console.log(`[facetAnalyzer] Extracted ${validatedFacets.length} valid facets`)
  return { facets: validatedFacets, visualChunks }
}

/**
 * Validate a single facet
 */
function validateFacet(
  facet: any,
  enabledFacetIds: Set<string>
): ExtractedFacet | null {
  // Required fields
  if (!facet.facetTypeId || typeof facet.facetTypeId !== 'string') {
    console.warn('[facetAnalyzer] Missing or invalid facetTypeId:', facet)
    return null
  }

  if (!enabledFacetIds.has(facet.facetTypeId)) {
    console.warn('[facetAnalyzer] Facet type not enabled:', facet.facetTypeId)
    return null
  }

  if (!facet.content || typeof facet.content !== 'string') {
    console.warn('[facetAnalyzer] Missing or invalid content:', facet)
    return null
  }

  if (!facet.evidence || typeof facet.evidence !== 'string') {
    console.warn('[facetAnalyzer] Missing or invalid evidence:', facet)
    return null
  }

  // Confidence must be 0-1
  const confidence = typeof facet.confidence === 'number'
    ? Math.max(0, Math.min(1, facet.confidence))
    : 0.5

  return {
    facetTypeId: facet.facetTypeId,
    title: facet.title || undefined,
    content: facet.content,
    evidence: facet.evidence,
    confidence,
    positionStart: typeof facet.positionStart === 'number' ? facet.positionStart : undefined,
    positionEnd: typeof facet.positionEnd === 'number' ? facet.positionEnd : undefined,
    suggestedEntityType: facet.suggestedEntityType || undefined,
    suggestedReason: facet.suggestedReason || undefined,
    problemContext: facet.problemContext || undefined,
    problemCause: facet.problemCause || undefined,
    problemConsequence: facet.problemConsequence || undefined,
  }
}

/**
 * Validate a visual chunk
 */
function validateVisualChunk(chunk: any): VisualChunk | null {
  if (!chunk || typeof chunk !== 'object') return null

  // Page number is required
  const pageNumber = typeof chunk.pageNumber === 'number' ? chunk.pageNumber : 1

  // Description is required
  if (!chunk.description || typeof chunk.description !== 'string') {
    return null
  }

  // Validate chunk type
  const validTypes = ['diagram', 'chart', 'canvas', 'whiteboard', 'screenshot', 'table', 'image', 'unknown']
  const chunkType = validTypes.includes(chunk.chunkType) ? chunk.chunkType : 'unknown'

  return {
    pageNumber,
    description: chunk.description,
    chunkType: chunkType as VisualChunk['chunkType'],
    textContext: chunk.textContext || undefined,
    confidence: typeof chunk.confidence === 'number' ? Math.max(0, Math.min(1, chunk.confidence)) : 0.7,
  }
}

/**
 * Attempt to repair truncated JSON from Claude responses
 */
function repairTruncatedJson(jsonStr: string): string {
  // If JSON ends mid-array, close it
  if (!jsonStr.trim().endsWith('}')) {
    // Find last complete object
    const lastCompleteObject = jsonStr.lastIndexOf('}')
    if (lastCompleteObject !== -1) {
      // Truncate to last complete object and close array
      jsonStr = jsonStr.substring(0, lastCompleteObject + 1)
      // Add closing brackets
      if (!jsonStr.includes(']')) {
        jsonStr += ']'
      }
      if (!jsonStr.endsWith('}')) {
        jsonStr += '}'
      }
    }
  }
  return jsonStr
}
